const DOMAIN_PATTERN =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** Canonical stored form: trimmed, lowercased, one leading "@" stripped. */
export function normalizeInviteDomain(raw: string): string {
  let domain = raw.trim().toLowerCase();
  if (domain.startsWith("@")) {
    domain = domain.slice(1);
  }
  return domain;
}

/** Normalizes every entry, drops blanks, dedupes preserving first-seen order. */
export function normalizeInviteDomains(raw: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of raw) {
    const normalized = normalizeInviteDomain(entry);
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

/** True when an ALREADY-NORMALIZED entry is storable (<= 253 chars, matches pattern). */
export function isValidInviteDomain(domain: string): boolean {
  return domain.length > 0 && domain.length <= 253 && DOMAIN_PATTERN.test(domain);
}

/** Everything after the LAST "@", trimmed and lowercased. "" when there is no "@". */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf("@");
  if (at === -1) {
    return "";
  }
  return email.slice(at + 1).trim().toLowerCase();
}

/**
 * Null/undefined/empty list => true. Otherwise exact match, re-normalizing each
 * stored entry so rows written outside the API still compare correctly.
 */
export function isEmailDomainAllowed(
  email: string,
  allowedDomains: string[] | null | undefined
): boolean {
  if (!allowedDomains || allowedDomains.length === 0) {
    return true;
  }
  const domain = emailDomain(email);
  if (!domain) {
    return false;
  }
  return allowedDomains.some(
    (entry) => normalizeInviteDomain(entry) === domain
  );
}

/** "@acme.com" | "@a.com or @b.com" | "@a.com, @b.com, or @c.com" */
export function formatAllowedDomainList(domains: string[]): string {
  const formatted = domains.map((d) => `@${d}`);
  if (formatted.length === 0) {
    return "";
  }
  if (formatted.length === 1) {
    return formatted[0];
  }
  if (formatted.length === 2) {
    return `${formatted[0]} or ${formatted[1]}`;
  }
  return `${formatted.slice(0, -1).join(", ")}, or ${formatted[formatted.length - 1]}`;
}

export const INVITE_DOMAIN_NOT_ALLOWED_CODE = "email_domain_not_allowed";

/** Single source of the rejection copy, shared by server and client. */
export function inviteDomainErrorMessage(domains: string[]): string {
  return `Invited users must have an email address ending in ${formatAllowedDomainList(domains)}.`;
}

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Lightweight format check shared by invite dialogs and invite API routes. */
export function isValidEmailFormat(email: string): boolean {
  return EMAIL_FORMAT.test(email);
}
